import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  CheckCircle2, AlertCircle, RefreshCw, Plus, Loader2,
  KeyRound, Pencil, Trash2, X, Check,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { formatDateISO } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";

const SOURCE_HINTS = {
  mysql: {
    fields: [
      { key: "mysql_host", label: "Host", placeholder: "myid-live.cvfpt3xnugok.us-west-2.rds.amazonaws.com" },
      { key: "mysql_port", label: "Port", placeholder: "3306" },
      { key: "mysql_user", label: "Username", placeholder: "readonly_user" },
      { key: "mysql_password", label: "Password", placeholder: "••••••••" },
      { key: "mysql_database", label: "Database name", placeholder: "live" },
      { key: "mysql_proxy_host", label: "SOCKS5 proxy host (optional)", placeholder: "52.12.60.253" },
      { key: "mysql_proxy_port", label: "SOCKS5 proxy port (optional)", placeholder: "1080" },
      { key: "mysql_proxy_user", label: "SOCKS5 proxy username (optional)", placeholder: "" },
      { key: "mysql_proxy_password", label: "SOCKS5 proxy password (optional)", placeholder: "••••••••" },
    ],
    docs: "Read-only credentials to your MyID MySQL database. This is the source of truth for subscription lifecycle across web, iOS, and Android. If your DB requires IP whitelisting, use the optional SOCKS5 proxy fields below.",
  },
  stripe: {
    fields: [{ key: "api_key", label: "Stripe secret key", placeholder: "sk_live_..." }],
    docs: "Settings → Developers → API keys in Stripe Dashboard.",
  },
  appstore: {
    fields: [
      { key: "issuer_id", label: "Issuer ID", placeholder: "57246542-…" },
      { key: "key_id", label: "Key ID", placeholder: "ABC123DEF4" },
      { key: "api_key", label: "Private key (.p8 contents)", placeholder: "-----BEGIN PRIVATE KEY-----" },
      { key: "vendor_number", label: "Vendor Number", placeholder: "85123456" },
    ],
    docs: "Issuer ID, Key ID, Private key: App Store Connect → Users and Access → Integrations → App Store Connect API. Vendor Number: App Store Connect → Reports → Sales and Trends (top of page).",
  },
  googleplay: {
    fields: [
      { key: "service_account_json", label: "Service account JSON", placeholder: "{ … }" },
      { key: "package_name", label: "Android package name", placeholder: "com.myidband.MyID" },
      { key: "bucket_name", label: "Reports bucket name", placeholder: "pubsite_prod_…" },
    ],
    docs: "Service account: Google Cloud → Service Accounts. Bucket name: Play Console → Download reports → Financial → the gs:// path. The service account must be granted access in Play Console → Users and permissions for the app, with permission to view financial data and bulk reports.",
  },
  shopify: {
    fields: [
      { key: "shop_domain", label: "Shop domain", placeholder: "getmyid.myshopify.com" },
      { key: "shopify_client_id", label: "Dev Dashboard Client ID", placeholder: "1295be162bb31fd23fcfd…" },
      { key: "shopify_client_secret", label: "Dev Dashboard Client secret", placeholder: "shpss_…" },
    ],
    docs: "Save these three fields first, then click \"Connect Shopify\" to authorize. The Dev Dashboard app's Allowed Redirect URLs must include this BI host's /api/sources/shopify/oauth/callback. Scopes needed: read_customers, read_orders, read_products.",
    oauth: true,
  },
};

// Human-readable one-liner shown under each source's card title. Keyed
// by source.key so Shopify no longer inherits the generic
// `{platform} subscriptions` template (which mislabelled it as
// "Web subscriptions" even though it's e-commerce, not subs).
const SOURCE_TAGLINE = {
  mysql: "MyID user database — subscriptions & upgrade requests",
  stripe: "Web subscriptions & one-off Stripe charges",
  appstore: "iOS subscriptions (App Store Connect)",
  googleplay: "Android subscriptions (Google Play Console)",
  shopify: "E-commerce orders & customers",
};

function SourceCard({ source, onSync, onSave }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState({});
  const hints = SOURCE_HINTS[source.key] || { fields: [] };
  const savedHints = source.credential_hints || {};
  const savedSet = source.credentials_set || {};

  const handleSync = async () => {
    setBusy(true);
    try {
      await onSync(source.key);
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      await onSave(source.key, creds);
      setOpen(false);
      setCreds({});
    } finally {
      setBusy(false);
    }
  };

  const status = source.last_sync_status || "success";
  const ok = status === "success";
  const inProgress = status === "in_progress";
  return (
    <Card data-testid={`source-card-${source.key}`}>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-display">{source.name}</CardTitle>
            <Badge
              variant={ok || inProgress ? "secondary" : "destructive"}
              className={
                inProgress
                  ? "bg-primary/10 text-primary"
                  : ok
                    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                    : ""
              }
              data-testid={`source-status-${source.key}`}
            >
              {inProgress ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Syncing
                </>
              ) : ok ? (
                <>
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Connected
                </>
              ) : (
                <>
                  <AlertCircle className="h-3 w-3 mr-1" /> Error
                </>
              )}
            </Badge>
          </div>
          <p
            className="text-xs text-muted-foreground mt-1"
            data-testid={`source-tagline-${source.key}`}
          >
            {SOURCE_TAGLINE[source.key] || `${source.platform} subscriptions`}
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-muted-foreground space-y-1">
          <p>
            Last sync:{" "}
            <span className="text-foreground font-medium num">{formatDateISO(source.last_sync_at)}</span>
          </p>
          {source.last_sync_message && (
            <p className="text-[11px] truncate">{source.last_sync_message}</p>
          )}
        </div>

        {/* Stored credentials summary */}
        {hints.fields.length > 0 && (
          <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1.5" data-testid={`source-stored-${source.key}`}>
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">
              Stored credentials
            </p>
            {hints.fields.map((f) => (
              <div key={f.key} className="flex items-start justify-between text-xs gap-3">
                <span className="text-muted-foreground shrink-0">{f.label}</span>
                {savedSet[f.key] ? (
                  <span className="inline-flex items-center gap-1.5 text-foreground num min-w-0 flex-1 justify-end">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                    <span
                      className="font-mono truncate"
                      title={savedHints[f.key] || ""}
                    >
                      {savedHints[f.key] || "•••••••"}
                    </span>
                  </span>
                ) : (
                  <span className="text-muted-foreground italic shrink-0">Not set</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={busy}
            data-testid={`sync-${source.key}-btn`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            Sync now
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen((s) => !s)}
            data-testid={`edit-source-${source.key}`}
          >
            {open ? "Cancel" : Object.keys(savedSet).length > 0 ? "Replace credentials" : "Add credentials"}
          </Button>
          {hints.oauth && (
            <Button
              variant="default"
              size="sm"
              disabled={busy}
              data-testid={`oauth-connect-${source.key}`}
              onClick={async () => {
                setBusy(true);
                try {
                  const r = await api.get(`/sources/${source.key}/oauth/start`);
                  if (r.data?.install_url) {
                    window.location.href = r.data.install_url;
                  } else {
                    toast.error("Could not start OAuth flow.");
                  }
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              <KeyRound className="h-3.5 w-3.5 mr-1" />
              {savedSet.access_token ? "Reconnect Shopify" : "Connect Shopify"}
            </Button>
          )}
        </div>
        {open && (
          <div className="border-t border-border pt-4 space-y-3" data-testid={`source-form-${source.key}`}>
            {hints.fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`${source.key}-${f.key}`} className="text-xs">
                  {f.label} {savedSet[f.key] && <span className="text-muted-foreground">(replace)</span>}
                </Label>
                <Input
                  id={`${source.key}-${f.key}`}
                  placeholder={savedSet[f.key] ? "Leave empty to keep existing" : f.placeholder}
                  value={creds[f.key] || ""}
                  onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                  data-testid={`source-input-${source.key}-${f.key}`}
                />
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">{hints.docs}</p>
            <Button size="sm" onClick={handleSave} disabled={busy || Object.values(creds).every((v) => !v)} data-testid={`save-source-${source.key}`}>
              Save credentials
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProfileCard() {
  const { user, refresh } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);

  // Password change state
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      if (name.trim() && name.trim() !== (user?.name || "")) payload.name = name.trim();
      if (email.trim() && email.trim().toLowerCase() !== (user?.email || "")) {
        payload.email = email.trim();
      }
      if (Object.keys(payload).length === 0) {
        setEditing(false);
        return;
      }
      await api.patch("/auth/me", payload);
      await refresh();
      toast.success("Profile updated");
      setEditing(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPwOpen(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Password change failed");
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <Card data-testid="profile-card">
      <CardHeader>
        <CardTitle className="font-display text-base">Your profile</CardTitle>
        <p className="text-xs text-muted-foreground">
          Update your name, email, and password.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Profile info */}
        {!editing ? (
          <div className="flex items-start justify-between gap-3" data-testid="profile-view">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground w-14">Name</span>
                <span className="text-sm font-medium" data-testid="profile-name">{user?.name || "—"}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground w-14">Email</span>
                <span className="text-sm font-mono num" data-testid="profile-email">{user?.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground w-14">Role</span>
                <Badge variant="secondary" className="bg-primary/10 text-primary capitalize" data-testid="profile-role">
                  {user?.role || "admin"}
                </Badge>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          </div>
        ) : (
          <form onSubmit={saveProfile} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="profile-form">
            <div className="space-y-1.5">
              <Label htmlFor="profile-name-input" className="text-xs">Name</Label>
              <Input
                id="profile-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="profile-email-input" className="text-xs">Email (used for login)</Label>
              <Input
                id="profile-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@myid.com"
                data-testid="profile-email-input"
              />
            </div>
            <div className="md:col-span-2 flex gap-2 pt-1">
              <Button type="submit" size="sm" disabled={saving} data-testid="save-profile-btn">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                Save
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => {
                setEditing(false);
                setName(user?.name || "");
                setEmail(user?.email || "");
              }} data-testid="cancel-profile-btn">
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Password change */}
        <div className="border-t border-border pt-4">
          {!pwOpen ? (
            <Button variant="outline" size="sm" onClick={() => setPwOpen(true)} data-testid="change-password-btn">
              <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Change password
            </Button>
          ) : (
            <form onSubmit={changePassword} className="space-y-3" data-testid="change-password-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="current-password" className="text-xs">Current password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    required
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                    data-testid="current-password-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new-password" className="text-xs">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password" className="text-xs">Confirm new password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    required
                    minLength={8}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={pwSaving} data-testid="save-password-btn">
                  {pwSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <KeyRound className="h-3.5 w-3.5 mr-1" />}
                  Update password
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => {
                  setPwOpen(false);
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                }} data-testid="cancel-password-btn">
                  Cancel
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                You will stay signed in on this device. Existing sessions on other devices remain valid until they expire.
              </p>
            </form>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TeamUserRow({ u, isMe, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(u.name || "");
  const [email, setEmail] = useState(u.email || "");
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [resetting, setResetting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (name.trim() && name.trim() !== (u.name || "")) payload.name = name.trim();
      if (email.trim().toLowerCase() !== (u.email || "")) payload.email = email.trim();
      if (Object.keys(payload).length === 0) { setEditing(false); return; }
      await api.patch(`/auth/users/${u.id}`, payload);
      toast.success("User updated");
      setEditing(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (resetPw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setResetting(true);
    try {
      await api.post(`/auth/users/${u.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${u.email}`);
      setResetPw("");
      setResetOpen(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success(`Removed ${u.email}`);
      setConfirmDelete(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <TableRow data-testid={`team-row-${u.id}`}>
        <TableCell>
          {editing ? (
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   className="h-8 text-sm" data-testid={`team-name-input-${u.id}`} />
          ) : (
            <span className="text-sm font-medium">{u.name || "—"}</span>
          )}
        </TableCell>
        <TableCell>
          {editing ? (
            <Input value={email} onChange={(e) => setEmail(e.target.value)}
                   className="h-8 text-sm font-mono" type="email"
                   data-testid={`team-email-input-${u.id}`} />
          ) : (
            <span className="text-sm font-mono num">{u.email}</span>
          )}
        </TableCell>
        <TableCell>
          <Badge variant="secondary" className="bg-primary/10 text-primary capitalize">
            {u.role}
          </Badge>
          {isMe && (
            <Badge variant="outline" className="ml-2 text-[10px]">You</Badge>
          )}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground num whitespace-nowrap">
          {formatDateISO(u.created_at)}
        </TableCell>
        <TableCell className="text-right">
          <div className="inline-flex gap-1">
            {editing ? (
              <>
                <Button size="sm" variant="outline" onClick={save} disabled={saving}
                        data-testid={`team-save-${u.id}`}>
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => {
                  setEditing(false);
                  setName(u.name || "");
                  setEmail(u.email || "");
                }} data-testid={`team-cancel-${u.id}`}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="ghost" onClick={() => setEditing(true)}
                        title="Edit" data-testid={`team-edit-${u.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                {!isMe && (
                  <Button size="sm" variant="ghost" onClick={() => setResetOpen(true)}
                          title="Reset password" data-testid={`team-reset-${u.id}`}>
                    <KeyRound className="h-3.5 w-3.5" />
                  </Button>
                )}
                {!isMe && (
                  <Button size="sm" variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setConfirmDelete(true)}
                          title="Remove user"
                          data-testid={`team-delete-${u.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </>
            )}
          </div>
        </TableCell>
      </TableRow>

      {resetOpen && (
        <TableRow data-testid={`team-reset-row-${u.id}`}>
          <TableCell colSpan={5} className="bg-muted/30">
            <form onSubmit={reset} className="flex flex-wrap items-end gap-2 py-2">
              <div className="space-y-1.5 flex-1 min-w-[200px]">
                <Label htmlFor={`reset-pw-${u.id}`} className="text-xs">
                  New password for {u.email}
                </Label>
                <Input id={`reset-pw-${u.id}`} type="password" required minLength={8}
                       value={resetPw} onChange={(e) => setResetPw(e.target.value)}
                       placeholder="At least 8 characters"
                       data-testid={`team-reset-input-${u.id}`} />
              </div>
              <Button type="submit" size="sm" disabled={resetting}
                      data-testid={`team-reset-submit-${u.id}`}>
                {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                Set password
              </Button>
              <Button type="button" size="sm" variant="ghost"
                      onClick={() => { setResetOpen(false); setResetPw(""); }}>
                Cancel
              </Button>
            </form>
          </TableCell>
        </TableRow>
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid={`confirm-delete-dialog-${u.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {u.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              They will lose access to the dashboard immediately. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid={`confirm-delete-cancel-${u.id}`}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              data-testid={`confirm-delete-yes-${u.id}`}
            >
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function TeamCard() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Card data-testid="team-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-display text-base">Team</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Admins who can view the dashboard, run syncs, and manage sources.
          </p>
        </div>
        <Button
          variant={showAdd ? "ghost" : "default"}
          size="sm"
          onClick={() => setShowAdd((s) => !s)}
          data-testid="toggle-add-admin"
        >
          {showAdd ? (
            <><X className="h-3.5 w-3.5 mr-1" /> Close</>
          ) : (
            <><Plus className="h-3.5 w-3.5 mr-1" /> Add admin</>
          )}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {showAdd && <AddAdminInline onAdded={() => { setShowAdd(false); load(); }} />}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [0, 1].map((i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}><Skeleton className="h-4 w-full" /></TableCell>
                  </TableRow>
                ))
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-sm text-muted-foreground text-center py-6">
                    No users yet.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TeamUserRow key={u.id} u={u} isMe={u.id === me?.id} onChanged={load} />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function AddAdminInline({ onAdded }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/register-admin", { email: email.trim(), name, password });
      toast.success("Admin user created");
      setEmail("");
      setName("");
      setPassword("");
      onAdded?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to add admin");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3 rounded-lg border border-border bg-muted/30 p-4" data-testid="add-admin-form">
      <div className="space-y-1.5">
        <Label htmlFor="new-admin-email" className="text-xs">Email</Label>
        <Input id="new-admin-email" type="email" required value={email}
               onChange={(e) => setEmail(e.target.value)}
               placeholder="teammate@myid.com" data-testid="add-admin-email" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="new-admin-name" className="text-xs">Name</Label>
        <Input id="new-admin-name" value={name} onChange={(e) => setName(e.target.value)}
               placeholder="Optional" data-testid="add-admin-name" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="new-admin-password" className="text-xs">Password</Label>
        <Input id="new-admin-password" type="password" required minLength={8}
               value={password} onChange={(e) => setPassword(e.target.value)}
               placeholder="At least 8 characters" data-testid="add-admin-password" />
      </div>
      <div className="flex items-end">
        <Button type="submit" disabled={submitting} className="w-full" data-testid="add-admin-submit">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add admin</>}
        </Button>
      </div>
    </form>
  );
}

export default function Settings() {
  const [sources, setSources] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([api.get("/sources"), api.get("/sync-runs")]);
      setSources(s.data);
      setRuns(r.data);
    } finally {
      setLoading(false);
    }
  };

  // Poll while any source is mid-sync.
  useEffect(() => {
    const inProgress = sources.some((s) => s.last_sync_status === "in_progress");
    if (!inProgress) return;
    const t = setInterval(async () => {
      try {
        const [s, r] = await Promise.all([api.get("/sources"), api.get("/sync-runs")]);
        setSources(s.data);
        setRuns(r.data);
      } catch {
        // ignore transient errors
      }
    }, 2500);
    return () => clearInterval(t);
  }, [sources]);

  useEffect(() => {
    load();
  }, []);

  const handleSync = async (key) => {
    try {
      const { data } = await api.post(`/sources/${key}/sync`);
      if (data.status === "in_progress") {
        toast.info(`${key} sync started — this may take a minute on the first run.`);
      } else {
        toast.success(`${key} sync completed`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Sync failed");
    }
  };

  const handleSave = async (key, creds) => {
    try {
      await api.put(`/sources/${key}`, creds);
      toast.success(`${key} credentials saved`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    }
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="settings-root">
        <div className="flex flex-col gap-2">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Admin</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Settings</h1>
        </div>

        <section className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-[0.16em]">
            Connected sources
          </h2>
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200 px-4 py-3 flex items-start gap-3" data-testid="ingestion-status-banner">
            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div className="text-xs leading-relaxed space-y-1">
              <p className="font-medium">
                Automatic daily sync at 9:00 PM MDT (3:00 UTC).
              </p>
              <p>
                All five sources run sequentially each night: MyID MySQL, Stripe, App Store Connect, Google Play, then Shopify — followed by an automatic Unified Customers rebuild so the /customers view reflects the new data. The first sync per source is a full backfill; subsequent runs are incremental. Use the &ldquo;Sync now&rdquo; button on any card to refresh a feed on demand.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {loading
              ? [0, 1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-5 space-y-3">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-3 w-40" />
                      <Skeleton className="h-8 w-28" />
                    </CardContent>
                  </Card>
                ))
              : sources.map((src) => (
                  <SourceCard key={src.key} source={src} onSync={handleSync} onSave={handleSave} />
                ))}
          </div>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <ProfileCard />
          <TeamCard />
        </section>

        <section>
          <Card data-testid="sync-runs-card">
            <CardHeader>
              <CardTitle className="font-display text-base">Sync history</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                The most recent sync runs across all sources.
              </p>
            </CardHeader>
            <CardContent className="px-0 md:px-2">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Records</TableHead>
                      <TableHead>Message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      [0, 1, 2].map((i) => (
                        <TableRow key={i}>
                          <TableCell colSpan={5}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : runs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-sm text-muted-foreground text-center py-6">
                          No sync runs yet. Trigger a sync above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      runs.map((r) => (
                        <TableRow key={r.id} data-testid={`sync-run-${r.id}`}>
                          <TableCell className="font-medium capitalize">{r.source_key}</TableCell>
                          <TableCell className="text-xs text-muted-foreground num">
                            {formatDateISO(r.started_at)}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="secondary"
                              className={
                                r.status === "success"
                                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                  : r.status === "in_progress"
                                    ? "bg-primary/10 text-primary"
                                    : "bg-destructive/10 text-destructive"
                              }
                            >
                              {r.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right num">{r.records_pulled}</TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate">
                            {r.message}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </AppShell>
  );
}
